
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Token } from '../../../types';
import { Info, ShieldCheck, Zap, ArrowRight } from 'lucide-react';

interface FeeCardProps {
    fromToken: Token | null;
    toToken: Token | null;
    amount: number;
    mode: 'swap' | 'bridge';
}

export const FeeCard: React.FC<FeeCardProps> = ({ fromToken, toToken, amount, mode }) => {
    if (!fromToken || !toToken || amount === 0) return null;

    const serviceFeePercent = mode === 'bridge' ? 0.001 : 0.025; 
    const serviceFee = amount * serviceFeePercent;
    const networkGas = fromToken.chain === 'SOL' ? 0.000005 : 0.001;
    const slippage = 0.05; 

    return (
        <div className="p-6 bg-stone-50 dark:bg-black/40 rounded-sm border border-stone-200 dark:border-white/5 space-y-5 shadow-inner">
            <div className="flex justify-between items-center group cursor-help">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-stone-500 font-black uppercase tracking-[0.2em]">Protocol Service Fee</span>
                    <Info size={12} className="text-stone-300 group-hover:text-nobel-gold transition-colors" />
                </div>
                <div className="text-right">
                    <div className="font-mono text-xs font-bold text-[#0A0B0F] dark:text-stone-200">
                        {serviceFee.toLocaleString(undefined, { maximumFractionDigits: 6 })} {fromToken.symbol}
                    </div>
                    <div className="text-[8px] font-black text-stone-400 uppercase tracking-tighter">{(serviceFeePercent * 100).toFixed(2)}% Applied</div>
                </div>
            </div>
            
            <div className="flex justify-between items-center">
                <span className="text-[10px] text-stone-500 font-black uppercase tracking-[0.2em]">Network Priority Gas</span>
                <span className="font-mono text-xs font-bold text-[#0A0B0F] dark:text-stone-200">
                    ~{networkGas} {fromToken.chain}
                </span>
            </div>

            <div className="flex justify-between items-center">
                <span className="text-[10px] text-stone-500 font-black uppercase tracking-[0.2em]">Slippage Tolerance</span>
                <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-emerald-500">{slippage}%</span>
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                </div>
            </div>

            <div className="pt-5 border-t border-stone-200 dark:border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-[9px] text-emerald-600 dark:text-emerald-400 font-black uppercase tracking-[0.3em]">
                    <ShieldCheck size={14} /> AI-Optimized Routing
                </div>
                <div className="flex items-center gap-1.5 text-[10px] font-mono text-stone-400 uppercase font-bold tracking-tighter">
                    Est. Time: <span className="text-[#0A0B0F] dark:text-white">~450ms</span>
                </div>
            </div>
        </div>
    );
};
